import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, FileTextIcon, MailIcon } from 'lucide-react';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { Frame } from '../components/Frame';
import { legalPages } from '../data/legal';
import { contact } from '../data/contact';
import type { LegalBlock, LegalDownload } from '../data/legal';
type LegalProps = {
  accent: 'violet' | 'signal';
};
export function Legal({
  accent
}: LegalProps) {
  const {
    slug
  } = useParams();
  const page = legalPages.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (page) document.title = `${page.title} · Konrad Walz`;
  }, [page]);
  if (!page) return <Navigate to="/" replace data-id="element-669" data-mp="3n88h0rczferx" />;
  return <div lang="de" data-accent={accent} className="min-h-screen w-full bg-white font-sans text-ink antialiased" data-id="element-670" data-mp="3n9jfcbnnrubn">
      <Nav data-id="element-671" data-mp="3s6rxic8t3ycw" />
      <main data-id="element-672" data-mp="3s9e6mgbcilx8">
        <Frame data-id="element-673" data-mp="3s94jd0hoqsv8">
          <div className="grid gap-10 px-5 pb-20 pt-12 md:grid-cols-[220px_minmax(0,1fr)] md:gap-16 md:px-6 md:pb-28 md:pt-20" data-id="element-674" data-mp="27kv07iot0hak">
            <aside data-id="element-675" data-mp="23ihwt0xj4vgy">
              <nav aria-label="Rechtliches" className="md:sticky md:top-28" data-id="element-676" data-mp="23j02sp9zuo22">
                <Link to="/" className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors duration-150 hover:text-ink" data-id="element-677" data-mp="2hi9g9h6tf42c">
                  <ArrowLeftIcon className="h-4 w-4" data-id="element-678" data-mp="1ovooifmfgpy9" />
                  Zur Startseite
                </Link>
                <ul className="mt-8 flex flex-wrap gap-2 md:flex-col md:gap-1" data-id="element-679" data-mp="1jwv7id7xmgtc">
                  {legalPages.map(p => {
                  const active = p.slug === page.slug;
                  return <li key={p.slug} data-id="element-680" data-mp="1tsf3ezmhxfi3">
                        <Link to={`/${p.slug}`} aria-current={active ? 'page' : undefined} className={`block rounded-[6px] px-3 py-2 text-[15px] transition-colors duration-150 ${active ? 'bg-canvas font-medium text-ink' : 'text-muted hover:text-ink'}`} data-id="element-681" data-mp="163vxgqqx5et6">
                          {p.label}
                        </Link>
                      </li>;
                })}
                </ul>
              </nav>
            </aside>

            <article className="max-w-[720px]" data-id="element-682" data-mp="2i8zhorcd00c5">
              <h1 className="font-display text-[38px] font-semibold leading-[1.05] tracking-[-0.04em] md:text-[52px]" data-id="element-683" data-mp="2iaakanu2j21o">
                {page.title}
              </h1>
              <div className="mt-10 space-y-10 md:mt-12" data-id="element-684" data-mp="2r8uuf0lkc3ea">
                {page.sections.map((section, i) => <section key={section.heading ?? i} data-id="element-685" data-mp="2w90g8055ivog" data-mpi={i}>
                    {section.heading && <h2 className="text-[19px] font-medium tracking-[-0.01em] text-ink md:text-[20px]" data-id="element-686" data-mp="6i3x3y1ikstu">{section.heading}</h2>}
                    <div className={`space-y-3 ${section.heading ? 'mt-3' : ''}`} data-id="element-687" data-mp="llx77x872ja9">
                      {section.blocks.map((block, j) => <Block key={j} block={block} data-id="element-688" data-mp="gnvhitg9jlpt" data-mpi={j} />)}
                    </div>
                  </section>)}
              </div>
              {page.downloads && <DownloadList downloads={page.downloads} data-id="element-689" data-mp="7vbme2z6jz9q" />}
            </article>
          </div>
        </Frame>
      </main>
      <Footer data-id="element-690" data-mp="2w8kgrehy95z" />
    </div>;
}
function DownloadList({
  downloads
}: {
  downloads: LegalDownload[];
}) {
  return <section aria-labelledby="downloads-title" className="mt-12" data-id="element-691" data-mp="3u0fazmr3rco2">
      <h2 id="downloads-title" className="text-[19px] font-medium tracking-[-0.01em] text-ink md:text-[20px]" data-id="element-692" data-mp="3p3ovb0j7mj36">
        Dokumente
      </h2>
      <ul className="mt-4 divide-y divide-line overflow-hidden rounded-[12px] bg-white shadow-[0_0_0_1px_rgba(70,28,64,0.08),0_10px_30px_-16px_rgba(70,28,64,0.18)]" data-id="element-693" data-mp="3l35042i75pjo">
        {downloads.map(d => {
        const requestHref = `mailto:${contact.email}?subject=${encodeURIComponent(`Anfrage: ${d.title}`)}`;
        return <li key={d.title} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-5 md:p-6" data-id="element-694" data-mp="1vbq6ek47dtbd">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[10px] bg-canvas text-ink" aria-hidden="true" data-id="element-695" data-mp="1hcyvt7ojuprz">
                <FileTextIcon className="h-5 w-5" data-id="element-696" data-mp="1lc7oe97ushly" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-697" data-mp="17m477wntze3d">
                <p className="text-[16px] font-medium text-ink" data-id="element-698" data-mp="17kazsf5e8pub">{d.title}</p>
                <p className="mt-0.5 text-[14px] leading-snug text-muted" data-id="element-699" data-mp="1ceozgtr39j4s">{d.description}</p>
              </div>
              {d.url ? <a href={d.url} download className="inline-flex shrink-0 items-center justify-center gap-2 rounded-[8px] bg-ink px-4 py-2.5 text-[14px] font-medium text-white transition-colors duration-150 hover:bg-ink/85" data-id="element-700" data-mp="12natc34mtl2o">
                  <DownloadIcon className="h-4 w-4" data-id="element-701" data-mp="tofqn10sqt44" />
                  {d.format} herunterladen
                </a> : <a href={requestHref} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-[8px] bg-white px-4 py-2.5 text-[14px] font-medium text-ink shadow-[inset_0_0_0_1px_rgba(13,13,18,0.18)] transition-colors duration-150 hover:bg-canvas" data-id="element-702" data-mp="52jeke7mwpbo">
                  <MailIcon className="h-4 w-4" data-id="element-703" data-mp="sydx1rgh6gfs" />
                  Auf Anfrage
                </a>}
            </li>;
      })}
      </ul>
    </section>;
}
function Block({
  block
}: {
  block: LegalBlock;
}) {
  if (Array.isArray(block)) {
    return <ul className="list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-ink/75 marker:text-muted" data-id="element-704" data-mp="xq5gzj54lnwy">
        {block.map(item => <li key={item} data-id="element-705" data-mp="1bqg72jym2yh2">{item}</li>)}
      </ul>;
  }
  return <p className="whitespace-pre-line text-[15px] leading-relaxed text-ink/75" data-id="element-706" data-mp="16s4xmac8apzd">{block}</p>;
}